import React, { useRef, useState, useEffect } from 'react';
import './MysticaReplica.css'; // Reusing the same CSS
import Carousel, { CarouselItem } from '../blocks/Carousel/Carousel';
import AnimatedList from '../blocks/AnimatedList/AnimatedList';
import MetaBalls from '../blocks/MetaBalls/MetaBalls';
import SpotlightCard from '../blocks/SpotlightCard/SpotlightCard';
import ClickSpark from '../blocks/ClickSpark/ClickSpark';
import BlurText from '../blocks/BlurText/BlurText';
import { CSSProperties } from 'react';
import { animated, useSpring } from '@react-spring/web';
import { detectBrowserLanguage, getTranslations, Language } from "../utils/localization";

// Video player styles
const videoContainerStyle: CSSProperties = {
  position: 'relative',
  width: '100%',
  overflow: 'hidden',
  borderRadius: '8px',
  marginTop: '1rem',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.5s ease-in-out',
};

const videoCinematicStyle: CSSProperties = {
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  maxWidth: '1200px',
  height: 'auto',
  zIndex: 1000,
  overflow: 'hidden',
  borderRadius: '8px',
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
};

const videoOverlayStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  zIndex: 999,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  opacity: 0,
  visibility: 'hidden',
  transition: 'opacity 0.3s ease, visibility 0.3s ease',
};

const closeBtnStyle: CSSProperties = {
  position: 'absolute',
  top: '15px',
  right: '15px',
  color: 'white',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  border: 'none',
  borderRadius: '50%',
  width: '40px',
  height: '40px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  zIndex: 1001,
  fontSize: '24px',
};

// Custom styles for the Scrollden page
const titleStyle: CSSProperties = {
  fontFamily: 'Cinzel, serif',
  fontWeight: 700,
  fontSize: '7rem',
  letterSpacing: '0.05em',
  color: 'var(--text-color)',
  textTransform: 'uppercase',
  margin: 0,
  padding: 0,
  lineHeight: 1.2,
  display: 'inline-block',
  width: '100%',
  textAlign: 'center'
};

const subtitleStyle: CSSProperties = {
  fontSize: '2.5rem',
  fontWeight: 400,
  lineHeight: 1.4,
  maxWidth: '1600px',
  margin: '2rem auto',
  height: '150px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const languageSwitcherStyle: CSSProperties = {
  position: 'fixed',
  top: '20px',
  right: '20px',
  zIndex: 1000,
  display: 'flex',
  borderRadius: '8px',
  overflow: 'hidden'
};

// Upcoming video message style
const upcomingVideoStyle: CSSProperties = {
  textAlign: 'center',
  margin: '15px 0',
  fontSize: '1.2rem',
  fontWeight: 500,
  color: '#266867',
  fontStyle: 'italic',
};

const Scrollden: React.FC = () => {
  // Video player state
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isStandardVideoPlaying, setIsStandardVideoPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const standardVideoRef = useRef<HTMLVideoElement>(null);
  const videoOverlayRef = useRef<HTMLDivElement>(null);

  // Language state
  const [language, setLanguage] = useState<Language>('en');
  const [translations, setTranslations] = useState(getTranslations('en').scrollden);
  
  // State to track collisions between side navigation and content
  const [sideNavVisible, setSideNavVisible] = useState(true);
  const sideNavRef = useRef<HTMLDivElement>(null);
  const collisionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Detect browser language on component mount
  useEffect(() => {
    const detectedLanguage = detectBrowserLanguage();
    setLanguage(detectedLanguage);
    setTranslations(getTranslations(detectedLanguage).scrollden);
  }, []);

  // Toggle language function
  const toggleLanguage = (lang: Language) => {
    setLanguage(lang);
    setTranslations(getTranslations(lang).scrollden);
  };

  // Handle video play/pause
  const handleVideoPlay = () => {
    if (!isVideoPlaying && standardVideoRef.current) {
      // Pause the standard video before entering cinematic mode
      standardVideoRef.current.pause();
      setIsStandardVideoPlaying(false); // Standard video paused explicitly

      const currentTime = standardVideoRef.current.currentTime;

      setIsVideoPlaying(true); // Activate cinematic mode state
      if (videoOverlayRef.current) {
        videoOverlayRef.current.style.opacity = '1';
        videoOverlayRef.current.style.visibility = 'visible';

        // Play cinematic video from the correct time
        setTimeout(() => {
          if (videoRef.current) {
            videoRef.current.currentTime = currentTime;
            videoRef.current.play().catch(e => console.error("Could not play cinematic video:", e));
          }
        }, 50); // Short delay for transition
      }
    }
  };

  const handleVideoClose = () => {
    let currentTime = 0;
    if (videoRef.current) {
      currentTime = videoRef.current.currentTime;
      videoRef.current.pause();
    }

    setIsVideoPlaying(false); // Deactivate cinematic mode state

    if (videoOverlayRef.current) {
      videoOverlayRef.current.style.opacity = '0';
      videoOverlayRef.current.style.visibility = 'hidden';
    }

    // Resume standard video from where cinematic left off
    if (standardVideoRef.current) {
        standardVideoRef.current.currentTime = currentTime;
        standardVideoRef.current.play().catch(e => console.error("Could not play standard video:", e));
        setIsStandardVideoPlaying(true); // Standard video is now playing
    }
  };

  // Triggered when standard video's NATIVE play button is clicked
  const handleStandardVideoPlay = () => {
    setIsStandardVideoPlaying(true);
    // Option 1: Automatically go cinematic (current behavior preserved slightly modified)
    handleVideoPlay();
    // Option 2: Just play standard video (comment out handleVideoPlay() above if you want this)
    // If choosing Option 2, the user must click the overlay/video area itself (not the play button)
    // to enter cinematic mode via the `handleVideoPlay` attached to the wrapper/overlay.
  };

  // Triggered when standard video's NATIVE pause button is clicked
  const handleStandardVideoPause = () => {
    setIsStandardVideoPlaying(false);
  };

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Handle ESC key to exit cinematic mode
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isVideoPlaying) {
        handleVideoClose();
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => {
      window.removeEventListener('keydown', handleEscKey);
    };
  }, [isVideoPlaying]);

  const sectionRefs = {
    video: useRef<HTMLElement>(null),
    introduction: useRef<HTMLElement>(null),
    gameplay: useRef<HTMLElement>(null),
    showcase: useRef<HTMLElement>(null)
  };

  const [activeSection, setActiveSection] = useState<number>(0);

  const scrollToSection = (item: string, index: number) => {
    const refKeys = Object.keys(sectionRefs);
    const ref = sectionRefs[refKeys[index] as keyof typeof sectionRefs];
    
    if (ref && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(index);
    }
  };

  // Update active section based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const sections = Object.values(sectionRefs);
      const scrollPosition = window.scrollY;
      const viewportHeight = window.innerHeight;
      
      // Set a minimum threshold of visibility for a section to be considered active
      // This helps with smaller sections
      const visibilityThreshold = 100; // px from top of viewport
      
      // Check each section
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i].current;
        if (!section) continue;
        
        const rect = section.getBoundingClientRect();
        
        // A section is active if its top is within the threshold from the viewport top
        // OR if we're within the section (between its top and bottom)
        if (
          (rect.top >= 0 && rect.top <= visibilityThreshold) ||
          (rect.top <= 0 && rect.bottom > visibilityThreshold)
        ) {
          setActiveSection(i);
          return;
        }
      }
      
      // If we get here, no section matches our primary criteria
      // Use a fallback approach based on which section occupies most of the viewport
      let maxVisibleSection = 0;
      let maxVisibleHeight = 0;
      
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i].current;
        if (!section) continue;
        
        const rect = section.getBoundingClientRect();
        
        // Calculate how much of the section is visible in the viewport
        const visibleTop = Math.max(0, rect.top);
        const visibleBottom = Math.min(viewportHeight, rect.bottom);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);
        
        if (visibleHeight > maxVisibleHeight) {
          maxVisibleHeight = visibleHeight;
          maxVisibleSection = i;
        }
      }
      
      setActiveSection(maxVisibleSection);
    };
    
    // Throttle the scroll handler to improve performance and reduce sensitivity
    let scrollTimeout: ReturnType<typeof setTimeout>;
    const throttledScrollHandler = () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(handleScroll, 50); // 50ms delay
    };
    
    window.addEventListener('scroll', throttledScrollHandler);
    handleScroll(); // Initial call
    
    return () => {
      window.removeEventListener('scroll', throttledScrollHandler);
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, []);

  // Check for collisions between side navigation and each section
  useEffect(() => {
    const checkCollision = () => {
      if (!sideNavRef.current) return;
      
      const sideNavRect = sideNavRef.current.getBoundingClientRect();
      let hasCollision = false;
      
      // Add a small margin to detect collision before actual overlap
      const collisionMargin = 20; // 20px margin
      
      // Check collision with each section
      Object.values(sectionRefs).forEach(sectionRef => {
        if (!sectionRef.current) return;
        
        const sectionRect = sectionRef.current.getBoundingClientRect();
        
        // Calculate horizontal overlap with margin
        const horizontalOverlap = !(
          sideNavRect.right < sectionRect.left - collisionMargin || 
          sideNavRect.left > sectionRect.right + collisionMargin
        );
        
        // Calculate vertical overlap (only consider sections in viewport)
        const verticalOverlap = !(
          sectionRect.bottom < 0 ||
          sectionRect.top > window.innerHeight
        );
        
        // If there's both horizontal and vertical overlap, we have a collision
        if (horizontalOverlap && verticalOverlap) {
          hasCollision = true;
        }
      });
      
      setSideNavVisible(!hasCollision);
    };
    
    // Debounced check collision
    const debouncedCheckCollision = () => {
      if (collisionTimeoutRef.current) {
        clearTimeout(collisionTimeoutRef.current);
      }
      
      collisionTimeoutRef.current = setTimeout(checkCollision, 100);
    };
    
    window.addEventListener('scroll', debouncedCheckCollision);
    window.addEventListener('resize', debouncedCheckCollision);
    
    // Initial check
    checkCollision();
    
    return () => {
      window.removeEventListener('scroll', debouncedCheckCollision);
      window.removeEventListener('resize', debouncedCheckCollision);
      
      if (collisionTimeoutRef.current) {
        clearTimeout(collisionTimeoutRef.current);
      }
    };
  }, []);

  const showcaseItems: CarouselItem[] = [
    {
      id: 1,
      title: translations.showcase.items.world.title,
      description: translations.showcase.items.world.description,
      image: "/images/projects/scrollden/showCase1.png",
      alt: translations.showcase.items.world.title
    },
    {
      id: 2,
      title: translations.showcase.items.building.title,
      description: translations.showcase.items.building.description,
      image: "/images/projects/scrollden/showCase2.png",
      alt: translations.showcase.items.building.title
    },
    {
      id: 3,
      title: translations.showcase.items.creatures.title,
      description: translations.showcase.items.creatures.description,
      image: "/images/projects/scrollden/showCase3.png",
      alt: translations.showcase.items.creatures.title
    },
    {
      id: 4,
      title: translations.showcase.items.adventure.title,
      description: translations.showcase.items.adventure.description,
      image: "/images/projects/scrollden/showCase4.png",
      alt: translations.showcase.items.adventure.title
    }
  ];

  return (
<ClickSpark
                shape="heart"
                sparkColor='#BD2735'
                sparkSize={8}
                sparkRadius={40}
                sparkCount={16}
                duration={500}
              >      {/* Video Overlay for Cinematic Mode */}
      <div
        ref={videoOverlayRef}
        style={{
          ...videoOverlayStyle,
          opacity: isVideoPlaying ? 1 : 0,
          visibility: isVideoPlaying ? 'visible' : 'hidden',
        }}
        onClick={handleVideoClose} // Close only when clicking the overlay itself
      >
        {isVideoPlaying && (
          <>
            <button
              style={closeBtnStyle}
              onClick={(e) => {
                  e.stopPropagation(); // Prevent overlay click when clicking button
                  handleVideoClose();
              }}
              aria-label="Close video"
            >
              ✕
            </button>
            <div
              style={videoCinematicStyle}
              onClick={(e) => e.stopPropagation()} // Prevent clicks inside video area from closing
            >
              <video
                ref={videoRef}
                controls
                autoPlay // autoPlay needed here for resuming/initial play
                style={{ width: '100%', display: 'block' }}
                // REMOVED the onPause handler that incorrectly closed the modal
              >
                <source src="/images/projects/scrollden/teaser.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </>
        )}
      </div>

      <div className="project-page mystica-page">
        <div className="home-link">
          <div className="metaball-wrapper">
            <MetaBalls 
              color="#266867" 
              enableTransparency={true}
              ballCount={8}
              animationSize={25}
              speed={0.2}
              cursorBallColor="#266867"
            />
          </div>
          <a href="/">Home</a>
        </div>

        {/* Language Switcher */}
        <div style={languageSwitcherStyle}>
          <button 
            className={`lang-btn ${language === 'en' ? 'active' : ''}`}
            onClick={() => toggleLanguage('en')}
          >
            EN
          </button>
          <button 
            className={`lang-btn ${language === 'zh' ? 'active' : ''}`}
            onClick={() => toggleLanguage('zh')}
          >
            中文
          </button>
        </div>

        <header className="project-header">
          <div className="container">
            <h1 style={titleStyle}>
              <a 
                href="https://pengguo.itch.io/scrollden" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <BlurText 
                  text={translations.title}
                  delay={150}
                  animateBy="letters"
                  direction="top"
                  className="title-container"
                  style={{fontSize: '7rem', lineHeight: 1.2}}
                />
              </a>
            </h1>
            <hr />
            <h2 style={subtitleStyle}>
              <BlurText 
                text={translations.subtitle}
                delay={100}
                animateBy="words"
                direction="bottom"
                className="description-container"
                style={{fontSize: '2.5rem', lineHeight: 1.4}}
              />
            </h2>
          </div>
        </header>

        <main className="project-content">
          <div className="container">
            <SpotlightCard
              className="project-section"
              spotlightColor="rgba(38, 104, 103, 0.15)"
            >
              <section ref={sectionRefs.video}>
                <h3>{translations.sections.video}</h3>
                <div
                  style={videoContainerStyle}
                >
                  <video
                    ref={standardVideoRef}
                    controls
                    style={{ width: '100%', display: 'block', cursor: 'pointer' }}
                    onPlay={handleStandardVideoPlay} // Handle native play button click
                    onPause={handleStandardVideoPause} // Handle native pause button click
                    poster="/images/projects/scrollden/showCase1.png"
                    preload="metadata"
                  >
                    <source src="/images/projects/scrollden/teaser.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                  {/* Play overlay - clicking this now triggers cinematic mode */}
                  {!isStandardVideoPlaying && (
                    <div
                      className="video-play-overlay"
                      onClick={handleVideoPlay} // Trigger cinematic ONLY from overlay click
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        background: 'rgba(0,0,0,0.3)',
                        cursor: 'pointer',
                        // No pointerEvents: 'none' needed, it should be clickable
                      }}
                    >
                      <div style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '50%',
                        background: 'rgba(38, 104, 103, 0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 0 20px rgba(0,0,0,0.3)',
                        pointerEvents: 'none', // Prevent this inner div from catching clicks meant for the overlay
                      }}>
                        <span style={{
                          borderTop: '15px solid transparent',
                          borderBottom: '15px solid transparent',
                          borderLeft: '25px solid white',
                          marginLeft: '5px',
                        }}></span>
                      </div>
                    </div>
                  )}
                </div>
                <p style={upcomingVideoStyle}>{translations.sections.upcomingVideo}</p>
              </section>
            </SpotlightCard>

            <SpotlightCard 
              className="project-section" 
              spotlightColor="rgba(38, 104, 103, 0.15)"
            >
              <section ref={sectionRefs.introduction}>
                <h3>{translations.navItems[1]}</h3>
                <p>
                  {translations.sections.introduction}
                </p>
              </section>
            </SpotlightCard>

            <SpotlightCard 
              className="project-section" 
              spotlightColor="rgba(38, 104, 103, 0.15)"
            >
              <section ref={sectionRefs.gameplay}>
                <h3>{translations.navItems[2]}</h3>
                <p>
                  {translations.sections.gameplay}
                </p>
              </section>
            </SpotlightCard>

            <SpotlightCard 
              className="project-section showcase-section" 
              spotlightColor="rgba(38, 104, 103, 0.15)"
            >
              <section ref={sectionRefs.showcase}>
                <h3>{translations.showcase.title}</h3>
                <div className="carousel-wrapper">
                  <Carousel 
                    items={showcaseItems}
                    baseWidth={800}
                    autoplay={true}
                    autoplayDelay={5000}
                    pauseOnHover={true}
                    loop={true}
                  />
                </div>
              </section>
            </SpotlightCard>
          </div>
        </main>

        <div 
          className="side-navigation" 
          ref={sideNavRef} 
          style={{ 
            opacity: sideNavVisible ? 1 : 0,
            visibility: sideNavVisible ? 'visible' : 'hidden',
            transition: 'opacity 0.3s ease-in-out, visibility 0.3s ease-in-out'
          }}
        >
          <AnimatedList
            items={translations.navItems}
            onItemSelect={(item, index) => {
              scrollToSection(item, index);
              setActiveSection(index);
            }}
            showGradients={false}
            enableArrowNavigation={true}
            className="mystica-nav"
            displayScrollbar={false}
            selectedIndex={activeSection}
          />
        </div>
      </div>
    </ClickSpark>
  );
};

export default Scrollden; 